<template>
  <div id="app">
    <div v-if="sessionExpired" class="session-expired-banner">
      Your session has expired. Please sign in again.
      <button class="butt session-dismiss-btn" @click="clearSessionExpired">Dismiss</button>
    </div>
    <router-view></router-view>
  </div>
</template>

<script>
  import { ref, computed, provide, watch, onUnmounted } from 'vue'
  import { auth, getUserUnitPref, setUserUnitPref, signOutUser, getNotifications, markNotificationRead } from './firebase/firebase.js'

  export default {
    name: 'App',
    setup() {
      const user          = ref(null)
      const unitPref      = ref('metric')
      const sessionExpired = ref(false)
      const notifications  = ref([])
      const unreadCount    = computed(() => notifications.value.filter(n => !n.read).length)
      let explicitSignOut  = false

      const clearSessionExpired = () => { sessionExpired.value = false }
      const markExplicitAuth = () => { explicitSignOut = true }
      const handleSignOut = () => { explicitSignOut = true; signOutUser() }

      const refreshNotifications = async (uid) => {
        try { notifications.value = await getNotifications(uid) } catch (_) {}
      }

      const markNotifRead = async (uid, notifId) => {
        try {
          await markNotificationRead(uid, notifId)
          notifications.value = notifications.value.map(n =>
            n.id === notifId ? { ...n, read: true } : n
          )
        } catch (_) {}
      }

      let previousUser = null
      const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
        if (previousUser !== null && currentUser === null && !explicitSignOut) {
          sessionExpired.value = true
        }
        explicitSignOut = false
        previousUser = currentUser
        user.value = currentUser
        if (currentUser) {
          await refreshNotifications(currentUser.uid)
        } else {
          notifications.value = []
        }
      })

      watch(user, async (newUser) => {
        if (newUser) {
          unitPref.value = await getUserUnitPref()
        } else {
          unitPref.value = 'metric'
        }
      })

      // Theme — default dark
      const isDark = ref(localStorage.getItem('theme') !== 'light')

      const applyTheme = (dark) => {
        document.documentElement.classList.toggle('light-theme', !dark)
      }

      applyTheme(isDark.value)

      const toggleTheme = () => {
        isDark.value = !isDark.value
        localStorage.setItem('theme', isDark.value ? 'dark' : 'light')
        applyTheme(isDark.value)
      }

      const setUnitPrefAndSave = async (pref) => {
        unitPref.value = pref
        await setUserUnitPref(pref)
      }

      provide('user', user)
      provide('isDark', isDark)
      provide('toggleTheme', toggleTheme)
      provide('unitPref', unitPref)
      provide('setUnitPref', setUnitPrefAndSave)
      provide('signOut', handleSignOut)
      provide('markExplicitAuth', markExplicitAuth)
      provide('sessionExpired', sessionExpired)
      provide('clearSessionExpired', clearSessionExpired)
      provide('notifications', notifications)
      provide('unreadCount', unreadCount)
      provide('refreshNotifications', refreshNotifications)
      provide('markNotifRead', markNotifRead)

      onUnmounted(() => { unsubscribe() })

      return { user, sessionExpired, clearSessionExpired }
    }
  }
</script>

<style>
  .session-expired-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    background-color: var(--col1);
    color: white;
    text-align: center;
    padding: 1vh 2vw;
    font-size: 1.8vh;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2vw;
  }
  .session-dismiss-btn {
    background: none;
    border-color: white;
    color: white;
    font-size: 1.6vh;
    padding: 0.3vh 1vw;
  }

  /* ── Dark theme (default) ── */
  :root {
    --light: whitesmoke;
    --dark: #0d0d0d;
    --dark-grey: darkgray;
    --col1: red;
    --col2: gold;
    --input-bg: rgba(255,255,255,0.07);
    --input-border: rgba(255,255,255,0.2);
  }

  /* ── Light theme ── */
  .light-theme {
    --light: #1a1a1a;
    --dark: #f2f2f2;
    --dark-grey: #777;
    --col1: #cc0000;
    --col2: #9a7000;
    --input-bg: rgba(0,0,0,0.05);
    --input-border: rgba(0,0,0,0.2);
  }

  body {
    margin: 0;
    text-align: center;
    background-color: var(--dark);
    color: var(--light);
    transition: background-color 0.25s, color 0.25s;
  }
  a {
    color: var(--light);
    cursor: pointer;
    text-decoration: none;
  }
  p, h1, h2, h3, h4 {
    padding: 0px;
    margin: 0px;
  }
  .butt {
    cursor: pointer;
    padding: .55vh;
    font-weight: bolder;
    font-size: 2.2vh;
    border: 3px solid black;
    border-radius: 13px;
  }
  .butt:hover {
    transform: scale(0.96);
  }
  .label {
    font-size: 2.34vh;
    font-weight: bold;
    text-align: left;
    color: var(--light);
    text-decoration: underline;
    margin-right: 1.22vw;
  }
  .aTag {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    border: 2.22px solid var(--col2);
    border-radius: 3px;
    padding: 0.5vh 1vw;
    margin: 0.5vh 0.5vw;
    font-size: 2.22vh;
    text-align: center;
    background-color: var(--light);
    color: var(--dark);
    position: relative;
    transition: transform 0.15s, background-color 0.15s;
  }
  .aTag.clickable { cursor: pointer; }
  .aTag.clickable:hover {
    background-color: var(--col2);
    transform: scale(1.5);
    z-index: 10;
  }
  input[type="text"], textarea { text-transform: none; }

  /* ── Light theme: global contrast fixes ── */
  .light-theme input,
  .light-theme textarea,
  .light-theme select {
    background: var(--input-bg);
    color: var(--light);
    border-color: var(--input-border);
  }
  .light-theme input::placeholder,
  .light-theme textarea::placeholder { color: #999; }
  .light-theme .butt {
    border-color: var(--light);
    color: var(--light);
  }
  .light-theme .section {
    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  }
  .light-theme #topStuff {
    border-bottom-color: rgba(0,0,0,0.12);
  }
  .light-theme #menuButt span { background: var(--light); }
  .light-theme #searchBtn { color: var(--light); }
</style>
